import { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useMutation, useQueries } from '@tanstack/react-query';
import { useNavigate } from 'react-router';

import { fetchRepairItemHistoryBySerialNo } from '#/api/aftersalesMgmt/repairItem/fetchHistoryBySerialNo';
import { getCustomerPortalSnUrl } from '#/config/customerPortal';
import { ROUTES } from '#/config/routes';
import { cn } from '#/lib/utils';
import useDetailTable from '#/hooks/table/useDetailTable';
import { Button, DESTRUCTIVE_GHOST_BUTTON_CLASS } from '#/modules/commonComponents/button';
import DetailTable from '#/modules/commonComponents/table/DetailTable';
import { createTipHeader } from '#/modules/commonComponents/table/tableColumnHelpers';
import TruncatedTextWithTooltip from '#/modules/commonComponents/table/TruncatedTextWithTooltip';
import { createRepairReportFromRepairLine } from
  '#/modules/crmMgmt/aftersales/repairRequest/helper/createRepairReportFromLine';
import {
  REPAIR_REQUEST_PRODUCT_UNMAPPED_LABEL,
} from '#/modules/crmMgmt/aftersales/repairRequest/helper/formSerialize';
import {
  canCreateRepairReportFromLine,
  getRepairLineItemId,
} from '#/modules/crmMgmt/aftersales/repairRequest/helper/repairLineItemAccess';
import { formatDateOrDash } from '#/utils/date';
import { toast } from '#/utils/toast';

function displayDash(value) {
  if (value == null || value === '') {
    return '—';
  }
  return value;
}

/** 已存明細兩欄皆空＝查無對照；有值的那欄仍顯示原值。 */
function displayProductPreviewField(row, field) {
  const code = row?.productCode == null ? '' : String(row.productCode).trim();
  const name = row?.itemName == null ? '' : String(row.itemName).trim();
  if (!code && !name) {
    return REPAIR_REQUEST_PRODUCT_UNMAPPED_LABEL;
  }
  return displayDash(field === 'itemName' ? name : code);
}

const REPORT_LOOKUP_STATUSES = new Set(['IN_PROGRESS', 'COMPLETED']);

/**
 * 只對到這一列的維修品項／這張報修單，不拿序號上最新的另一張報告。
 *
 * @param {object[]} historyRows
 * @param {{ repairItemId?: string|null, serialNo?: string, repairRequestNo?: string|null }} spec
 * @returns {string|null}
 */
function findReportIdForLine(historyRows, spec) {
  const repairItemId = spec.repairItemId ?? null;
  const serialNo = String(spec.serialNo ?? '').trim();
  const repairRequestNo = String(spec.repairRequestNo ?? '').trim();
  const matched = (historyRows ?? []).find((entry) => {
    const item = entry?.repairItem ?? entry?.repair_item ?? null;
    if (!item?.id || !entry?.report?.id) {
      return false;
    }
    // 已取消的草稿不算有效報告：品項回待處理，可再建立新草稿
    if (entry.report.status === 'CANCELLED') {
      return false;
    }
    if (repairItemId && item.id !== repairItemId) {
      return false;
    }
    const itemSn = String(item.serialNo ?? '').trim();
    if (serialNo && itemSn && itemSn !== serialNo) {
      return false;
    }
    const itemReqNo = String(item.repairRequestNo ?? '').trim();
    if (repairRequestNo && itemReqNo && itemReqNo !== repairRequestNo) {
      return false;
    }
    return true;
  });
  return matched?.report?.id ?? null;
}

function buildLineColumns({
  showLineActions,
  requestStatus,
  creatingLineKey,
  existingReportIdByLineKey,
  latestLoadingByLineKey,
  onCreateReport,
  onOpenReportDetail,
  lineCorrectionActions,
}) {
  function renderReportAction(row, line) {
    const canCreate = canCreateRepairReportFromLine(requestStatus, line);
    const lineKey = String(line.id ?? row.id);
    const isCreating = creatingLineKey === lineKey;
    const existingReportId = existingReportIdByLineKey?.[lineKey] ?? null;
    const isLatestLoading = Boolean(latestLoadingByLineKey?.[lineKey]);

    if (isLatestLoading) {
      return null;
    }

    if (existingReportId) {
      return (
        <Button
          type="button"
          size="sm"
          variant="outline"
          className={cn('whitespace-nowrap')}
          onClick={(event) => {
            event.stopPropagation();
            onOpenReportDetail?.(existingReportId);
          }}
        >
          查看報告
        </Button>
      );
    }

    if (!canCreate) {
      return null;
    }

    return (
      <Button
        type="button"
        size="sm"
        className={cn('whitespace-nowrap')}
        disabled={Boolean(creatingLineKey)}
        onClick={(event) => {
          event.stopPropagation();
          onCreateReport?.(line, lineKey);
        }}
      >
        {isCreating ? '建立報告中…' : '建立報告'}
      </Button>
    );
  }

  const actionColumn = showLineActions
    ? [{
      id: 'createReport',
      header: createTipHeader('操作', '處理中可建立報告、更正序號或移除品項；已有報告可查看'),
      size: lineCorrectionActions ? 260 : 140,
      minSize: 120,
      enableSorting: false,
      cell: ({ row }) => {
        const line = row.original;
        const reportAction = renderReportAction(row, line);
        if (!lineCorrectionActions) {
          return reportAction;
        }
        return (
          <span className="inline-flex flex-wrap items-center gap-1.5">
            {reportAction}
            <Button
              type="button"
              size="sm"
              variant="outline"
              className={cn('whitespace-nowrap')}
              disabled={lineCorrectionActions.isPending}
              onClick={(event) => {
                event.stopPropagation();
                lineCorrectionActions.openCorrectSerial?.(line);
              }}
            >
              更正序號
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className={cn('whitespace-nowrap', DESTRUCTIVE_GHOST_BUTTON_CLASS)}
              disabled={lineCorrectionActions.isPending}
              onClick={(event) => {
                event.stopPropagation();
                lineCorrectionActions.openRemoveLine?.(line);
              }}
            >
              移除
            </Button>
          </span>
        );
      },
    }]
    : [];


  return [
    ...actionColumn,
    {
      id: 'serialNo',
      header: '產品序號',
      accessorKey: 'serialNo',
      accessorFn: (row) => displayDash(row.serialNo),
      size: 140,
      cell: ({ row, getValue }) => {
        const portalUrl = row.original.mtrSnId
          ? getCustomerPortalSnUrl(row.original.serialNo)
          : null;
        if (!portalUrl) {
          return getValue();
        }
        return (
          <span className="gap-1.5 inline-flex items-center">
            {getValue()}
            <a
              href={portalUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-primary underline-offset-2 hover:underline"
              onClick={(event) => event.stopPropagation()}
            >
              客戶站
            </a>
          </span>
        );
      },
    },
    {
      id: 'mtrSnId',
      header: '序號登錄',
      accessorFn: (row) => (row.mtrSnId ? String(row.mtrSnId) : '文字序號'),
      size: 160,
    },
    {
      id: 'problemDescription',
      header: '問題描述',
      accessorFn: (row) => displayDash(row.problemDescription),
      size: 200,
      meta: { cellClassName: 'td_left' },
      cell: ({ getValue }) => <TruncatedTextWithTooltip text={getValue()} />,
    },
    {
      id: 'accessories',
      header: '配件',
      accessorFn: (row) => displayDash(row.accessories),
      size: 140,
      meta: { cellClassName: 'td_left' },
      cell: ({ getValue }) => (
        <TruncatedTextWithTooltip
          text={getValue()}
          contentClassName="whitespace-pre-line"
        />
      ),
    },
    {
      id: 'productCode',
      header: '產品代號',
      accessorFn: (row) => displayProductPreviewField(row, 'productCode'),
      size: 120,
    },
    {
      id: 'itemName',
      header: '品名',
      accessorFn: (row) => displayProductPreviewField(row, 'itemName'),
      size: 160,
      meta: { cellClassName: 'td_left' },
      cell: ({ getValue }) => <TruncatedTextWithTooltip text={getValue()} />,
    },
    {
      id: 'salesDate',
      header: '銷售日',
      accessorFn: (row) => formatDateOrDash(row.salesDate),
      size: 110,
    },
    {
      id: 'warrantyExpiryDate',
      header: '保固迄日',
      accessorFn: (row) => formatDateOrDash(row.warrantyExpiryDate),
      size: 110,
    },
    {
      id: 'remark',
      header: '明細備註',
      accessorFn: (row) => displayDash(row.remark),
      size: 140,
      meta: { cellClassName: 'td_left' },
      cell: ({ getValue }) => <TruncatedTextWithTooltip text={getValue()} />,
    },
    {
      id: 'version',
      header: '版本',
      accessorFn: (row) => displayDash(row.version),
      size: 100,
    },
  ];
}

const RepairRequestLinesTable = ({
  lines = [],
  requestStatus = null,
  repairRequestNo = null,
  showLineActions = false,
  lineCorrectionActions = null,
}) => {
  const navigate = useNavigate();

  const rows = useMemo(
    () => (lines ?? []).map((line, index) => ({
      ...line,
      id: line?.id ? String(line.id) : `line-${index}`,
    })),
    [lines],
  );

  const latestQuerySpecs = useMemo(() => {
    if (!showLineActions || !REPORT_LOOKUP_STATUSES.has(requestStatus)) {
      return [];
    }
    return rows
      .map((line) => {
        const serialNo = String(line?.serialNo ?? '').trim();
        if (!serialNo) {
          return null;
        }
        return {
          lineKey: String(line.id),
          serialNo,
          repairItemId: getRepairLineItemId(line),
          repairRequestNo,
        };
      })
      .filter(Boolean);
  }, [showLineActions, requestStatus, repairRequestNo, rows]);

  const latestQueries = useQueries({
    queries: latestQuerySpecs.map((spec) => ({
      queryKey: [
        'erp',
        'repairReport',
        'forRepairLine',
        spec.serialNo,
        spec.repairItemId,
        spec.repairRequestNo,
      ],
      queryFn: async () => {
        const history = await fetchRepairItemHistoryBySerialNo({
          serialNo: spec.serialNo,
          page: 1,
          pageSize: 50,
        });
        return findReportIdForLine(history?.data ?? [], spec);
      },
      staleTime: 30_000,
      retry: false,
    })),
  });

  const existingReportIdByLineKey = useMemo(() => {
    const map = {};
    latestQuerySpecs.forEach((spec, index) => {
      const query = latestQueries[index];
      if (!query?.isSuccess) {
        return;
      }
      if (query.data) {
        map[spec.lineKey] = query.data;
      }
    });
    return map;
  }, [latestQuerySpecs, latestQueries]);

  const latestLoadingByLineKey = useMemo(() => {
    const map = {};
    latestQuerySpecs.forEach((spec, index) => {
      const query = latestQueries[index];
      map[spec.lineKey] = Boolean(query?.isPending || query?.isLoading);
    });
    return map;
  }, [latestQuerySpecs, latestQueries]);

  const createMutation = useMutation({
    mutationFn: async ({ line }) => createRepairReportFromRepairLine(line, {
      repairRequestNo,
    }),
    onSuccess: (result) => {
      if (result?.noDuplicated) {
        return;
      }
      if (result?.alreadyExists) {
        if (result.existingId) {
          navigate(ROUTES.repairReportDetail(result.existingId));
          return;
        }
        toast.info('已有報告但無法取得 id，請至維修報告列表尋找');
        navigate(ROUTES.repairReport);
        return;
      }
      if (!result?.created) {
        return;
      }
      const createdId = result?.data?.id;
      toast.success(result?.message || '已建立維修報告草稿');
      if (createdId) {
        navigate(ROUTES.repairReportEdit(createdId));
      }
    },
  });

  const creatingLineKey = createMutation.isPending
    ? createMutation.variables?.lineKey
    : null;

  const handleCreateReport = useCallback((line, lineKey) => {
    createMutation.mutate({ line, lineKey });
  }, [createMutation]);

  const handleOpenReportDetail = useCallback((reportId) => {
    navigate(ROUTES.repairReportDetail(reportId));
  }, [navigate]);

  const columns = useMemo(
    () => buildLineColumns({
      showLineActions,
      requestStatus,
      creatingLineKey,
      existingReportIdByLineKey,
      latestLoadingByLineKey,
      onCreateReport: handleCreateReport,
      onOpenReportDetail: handleOpenReportDetail,
      lineCorrectionActions,
    }),
    [
      showLineActions,
      requestStatus,
      creatingLineKey,
      existingReportIdByLineKey,
      latestLoadingByLineKey,
      handleCreateReport,
      handleOpenReportDetail,
      lineCorrectionActions,
    ],
  );

  const detailTableState = useDetailTable(rows, columns, {
    enableExpanding: false,
    getRowId: (row) => String(row.id),
  });

  return (
    <div className="detail-table-container">
      <DetailTable
        state={detailTableState}
        containerClassName="detail-table-scroll--section-embedded"
        tableClassName="detail-table--flat-lines"
      />
    </div>
  );
};

RepairRequestLinesTable.propTypes = {
  lines: PropTypes.arrayOf(PropTypes.object),
  requestStatus: PropTypes.string,
  /** 供缺 repairItemId 時以 SN 歷史對齊報修單號 */
  repairRequestNo: PropTypes.string,
  /** 詳情明細：顯示操作欄。不能建立時不留停用鈕；有報告才顯示查看。 */
  showLineActions: PropTypes.bool,
  /** 處理中：每列多「更正序號／移除」 */
  lineCorrectionActions: PropTypes.shape({
    isPending: PropTypes.bool,
    openCorrectSerial: PropTypes.func,
    openRemoveLine: PropTypes.func,
  }),
};

export default RepairRequestLinesTable;
